import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { Card } from '~/components/isolated/CategoryBlocks/shared/Card';
import {
  GridView,
  tileClassNames,
} from '~/components/isolated/CategoryBlocks/shared/GridView';
import { IconButton } from '~/components/isolated/IconButton';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { Category } from '~/stores/CategoriesStore';

import styles from './Body.module.scss';

const Body = ({ items }: { items: Category[] }) => {
  const { isMobile } = useGlobal();

  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const children = items.map((category) => (
    <Card
      key={category.id}
      category={category}
      classes={{
        root: styles.card,
        titleClassName: styles.cardTitle,
        tileClassName: tileClassNames[1],
      }}
    />
  ));

  if (isMobile) {
    return <GridView className={styles.root}>{children.slice(0, 8)}</GridView>;
  }

  const carouselItems = children.map((child, index) => ({
    id: index,
    node: child,
  }));

  const showArrows = carouselItems.length > 4;

  return (
    <div className={styles.root}>
      <div className="carousel">
        {showArrows && (
          <IconButton
            icon="chevron-3.1"
            iconSize={14}
            size="XL"
            variant="shadowS"
            onClick={onPrevButtonClick}
            className={classes([
              styles.carouselPrevButton,
              prevBtnDisabled && styles.hidden,
            ])}
          />
        )}
        <Carousel
          opts={{
            align: 'start',
            slidesToScroll: 4,
            containScroll: 'trimSnaps',
          }}
          setApi={setApi}
          className={styles.slider}
        >
          <CarouselContent className={styles.carouselContent}>
            {carouselItems.map((item) => (
              <CarouselItem key={item.id} className={styles.slide}>
                {item.node}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {showArrows && (
          <IconButton
            icon="chevron-3.1"
            iconSize={14}
            size="XL"
            variant="shadowS"
            onClick={onNextButtonClick}
            className={classes([
              styles.carouselNextButton,
              nextBtnDisabled && styles.hidden,
            ])}
          />
        )}
      </div>
    </div>
  );
};

export default observer(Body);
